import {
  isMobileSession,
  performMobileLogout,
} from "../../components/auth/mobile-session";
import { useApolloClient } from "@apollo/client";
import { createContext, useContext, useReducer } from "react";
import { _getStudentMyProfile, _me } from "../gql/auth";
import {
  IAuthContext,
  IGetStudentMyProfile,
  IInfoContext,
  IMeRes,
  IUser,
  IUserAccount,
  IProfile,
} from "../types/types";
import { smartMatrixUrl } from "../smartmatrix/sso";

const _auth: IAuthContext = {
  auth: false,
  init: false,
  _id: "",
  accessToken: "",
  fname: "",
  lname: "",
  email: "",
  role: "",
  status: "",
  created_at: 0,
  updated_at: 0,
  profile: null,
  login: () => {},
  clearSession: () => {},
  refreshSession: async () => {},
  logout: () => {},
  me: () => {},
};
export const AuthContext = createContext<IAuthContext>(_auth);

const reducer = (state: IInfoContext, action: any) => {
  switch (action.type) {
    case "set":
      return { ...action.payload, init: true };
    case "reset":
      return { ...action.payload, init: true };
  }
  return state;
};

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const [_state, dispatch] = useReducer(reducer, {
    ..._auth,
    init: !localStorage.getItem(process.env.REACT_APP_LOCAL_TOKEN || ""),
  });
  const client = useApolloClient();

  AuthContext.displayName = "Auth Context";
  const refreshSession = async (expectedId?: string) => {
    const account = await client.query<IMeRes>({
      query: _me,
      fetchPolicy: "network-only",
    });
    const result = await client.query<IGetStudentMyProfile>({
      query: _getStudentMyProfile,
      fetchPolicy: "network-only",
    });
    const profile = result.data.getStudentMyProfile.profile;
    const profileAccount = result.data.getStudentMyProfile.account;
    if (
      !account.data.me?._id ||
      !profile ||
      (profileAccount?._id && profileAccount._id !== account.data.me._id)
    )
      throw new Error("Account refresh failed");
    if (
      expectedId &&
      (profileAccount?.smartmatrix_id !== expectedId ||
        profile.smartmatrix_id !== expectedId)
    )
      throw new Error(
        "Saved identity could not be verified in account and profile",
      );
    dispatch({
      type: "set",
      payload: {
        ...account.data.me,
        ...profileAccount,
        auth: true,
        accessToken: localStorage.getItem(
          process.env.REACT_APP_LOCAL_TOKEN || "",
        ),
        profile,
      },
    });
  };
  const me = async () => {
    try {
      const account = await client.query<IMeRes>({
        query: _me,
        fetchPolicy: "network-only",
      });
      const profile = await client.query<IGetStudentMyProfile>({
        query: _getStudentMyProfile,
        fetchPolicy: "network-only",
      });
      const token = localStorage.getItem(
        process.env.REACT_APP_LOCAL_TOKEN || "",
      );
      await dispatch({
        type: "set",
        payload: {
          ...account.data.me,
          auth: true,
          accessToken: token,
          profile: profile.data.getStudentMyProfile.profile,
        },
      });
    } catch (error) {
      localStorage.removeItem(process.env.REACT_APP_LOCAL_TOKEN || "");
      await dispatch({ type: "reset", payload: _auth });
      throw error;
    }
  };

  const login = async (
    user: Pick<IUser, "accessToken">,
    snapshot?: IUserAccount & { profile: IProfile },
  ) => {
    if (snapshot) {
      localStorage.setItem(
        process.env.REACT_APP_LOCAL_TOKEN || "",
        user.accessToken,
      );
      dispatch({
        type: "set",
        payload: { ...snapshot, auth: true, accessToken: user.accessToken },
      });
      return;
    }
    const key = process.env.REACT_APP_LOCAL_TOKEN || "";
    const previousToken = localStorage.getItem(key);
    try {
      localStorage.setItem(key, user.accessToken);
      await refreshSession();
    } catch (error) {
      if (previousToken) localStorage.setItem(key, previousToken);
      else localStorage.removeItem(key);
      await client.clearStore();
      throw error;
    }
  };

  const clearSession = async () => {
    sessionStorage.removeItem("smartmatrix_sso_session");
    localStorage.removeItem(process.env.REACT_APP_LOCAL_TOKEN || "");
    await dispatch({ type: "reset", payload: _auth });
  };
  const logout = async () => {
    if (
      isMobileSession(
        localStorage.getItem(process.env.REACT_APP_LOCAL_TOKEN || ""),
      )
    ) {
      let origin = "";
      try {
        origin = new URL(smartMatrixUrl("mobile/logout")).origin;
      } catch {
        /* Still clear local state. */
      }
      await performMobileLogout(client, origin, clearSession, "/auth/login");
      return;
    }
    const wasSso = sessionStorage.getItem("smartmatrix_sso_session") === "1";
    await clearSession();
    if (wasSso) {
      try {
        window.location.assign(smartMatrixUrl("logout"));
      } catch (_) {
        window.location.assign("/auth/login");
      }
    } else {
      window.location.assign("/auth/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{ ..._state, login, clearSession, logout, me, refreshSession }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const auth = useContext(AuthContext);
  return auth;
}

export default useAuth;
